<template>
  <v-container>
    <v-card>
      <v-toolbar
        color="bg-primary-lighten-1"
        density="compact"
        class="px-5 py-3"
      >
        <v-btn
          icon="mdi-arrow-left"
          variant="text"
          class="me-2"
          @click="$router.back()"
        ></v-btn>
        <v-toolbar-title class="text-h5 font-weight-medium">
          {{ $t('packages.detailsTitle') }}
        </v-toolbar-title>
      </v-toolbar>

      <v-card-text v-if="loading">
        <div class="d-flex justify-center align-center pa-6">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
          ></v-progress-circular>
        </div>
      </v-card-text>

      <template v-else-if="packageData">
        <!-- Package ID and Status -->
        <v-card-text class="pb-0">
          <div class="d-flex flex-wrap justify-space-between align-center">
            <div>
              <div class="text-subtitle-1 text-medium-emphasis">{{ $t('packages.id') }}</div>
              <div class="d-flex align-center">
                <span class="text-h6 font-weight-bold">{{ packageData.order_uuid }}</span>
                <v-btn
                  icon="mdi-content-copy"
                  variant="text"
                  size="small"
                  class="ms-2"
                  @click="copyToClipboard(packageData.order_uuid, $t('packages.id'))"
                ></v-btn>
              </div>
            </div>
            <v-chip
              :color="getStatusColor(packageData.status)"
              :class="getStatusClass(packageData.status)"
              size="large"
              class="mt-2 mt-sm-0"
            >
              {{ packageData.status }}
            </v-chip>
          </div>
        </v-card-text>

        <v-divider class="my-4"></v-divider>

        <!-- Package Details -->
        <v-card-text>
          <v-row>
            <v-col cols="12" md="4">
              <v-list>
                <v-list-subheader class="text-uppercase font-weight-bold">
                  {{ $t('packages.basicInfo') }}
                </v-list-subheader>
                
                <v-list-item>
                  <template v-slot:prepend>
                    <v-icon icon="mdi-package-variant" color="primary"></v-icon>
                  </template>
                  <v-list-item-title>{{ $t('packages.type') }}</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-chip
                      :color="getTypeColor(packageData.parcel_type)"
                      :class="getTypeClass(packageData.parcel_type)"
                      size="small"
                    >
                      {{ packageData.parcel_type || 'N/A' }}
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item>
                
                <v-list-item v-if="packageData.price">
                  <template v-slot:prepend>
                    <v-icon icon="mdi-cash" color="primary"></v-icon>
                  </template>
                  <v-list-item-title>{{ $t('packages.price') }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ packageData.price }} {{ packageData.price_currency || '₽' }}
                  </v-list-item-subtitle>
                </v-list-item>
                
                <v-list-item>
                  <template v-slot:prepend>
                    <v-icon icon="mdi-calendar" color="primary"></v-icon>
                  </template>
                  <v-list-item-title>{{ $t('packages.createdAt') }}</v-list-item-title>
                  <v-list-item-subtitle>{{ formatCreatedAt(packageData.created_at) }}</v-list-item-subtitle>
                </v-list-item>
                
                <v-list-item v-if="packageData.expiration_date || packageData.expires_at">
                  <template v-slot:prepend>
                    <v-icon icon="mdi-clock-outline" color="primary"></v-icon>
                  </template>
                  <v-list-item-title>{{ $t('packages.expiresAt') }}</v-list-item-title>
                  <v-list-item-subtitle>{{ formatCreatedAt(packageData.expiration_date || packageData.expires_at) }}</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-col>
            
            <v-col cols="12" md="4">
              <v-list>
                <v-list-subheader class="text-uppercase font-weight-bold">
                  {{ $t('packages.contactInfo') }}
                </v-list-subheader>
                
                <v-list-item>
                  <template v-slot:prepend>
                    <v-icon icon="mdi-account-arrow-up" color="primary"></v-icon>
                  </template>
                  <v-list-item-title>{{ $t('packages.sender') }}</v-list-item-title>
                  <v-list-item-subtitle>
                    <div class="user-info">
                      <div class="user-name-line">{{ getUserName(packageData.sender) }}</div>
                      <div v-if="getUserPhone(packageData.sender)" class="user-phone-line">
                        {{ getUserPhone(packageData.sender) }}
                        <v-btn
                          v-if="getUserPhone(packageData.sender)"
                          icon="mdi-content-copy"
                          variant="text"
                          size="x-small"
                          density="compact"
                          @click="copyToClipboard(getUserPhone(packageData.sender), $t('packages.phone'))"
                        ></v-btn>
                      </div>
                    </div>
                  </v-list-item-subtitle>
                </v-list-item>
                
                <v-list-item v-if="packageData.courier">
                  <template v-slot:prepend>
                    <v-icon icon="mdi-truck-delivery" color="primary"></v-icon>
                  </template>
                  <v-list-item-title>{{ $t('packages.courier') }}</v-list-item-title>
                  <v-list-item-subtitle>{{ packageData.courier || 'N/A' }}</v-list-item-subtitle>
                </v-list-item>
                
                <v-list-item>
                  <template v-slot:prepend>
                    <v-icon icon="mdi-account-arrow-down" color="primary"></v-icon>
                  </template>
                  <v-list-item-title>{{ $t('packages.recipient') }}</v-list-item-title>
                  <v-list-item-subtitle>
                    <div class="user-info">
                      <div class="user-name-line">{{ getUserName(packageData.recipient) }}</div>
                      <div v-if="getUserPhone(packageData.recipient)" class="user-phone-line">
                        {{ getUserPhone(packageData.recipient) }}
                        <v-btn
                          v-if="getUserPhone(packageData.recipient)"
                          icon="mdi-content-copy"
                          variant="text"
                          size="x-small"
                          density="compact"
                          @click="copyToClipboard(getUserPhone(packageData.recipient), $t('packages.phone'))"
                        ></v-btn>
                      </div>
                    </div>
                  </v-list-item-subtitle>
                </v-list-item>
                
                <v-list-item v-if="packageData.phone">
                  <template v-slot:prepend>
                    <v-icon icon="mdi-phone" color="primary"></v-icon>
                  </template>
                  <v-list-item-title>{{ $t('packages.phone') }}</v-list-item-title>
                  <v-list-item-subtitle class="d-flex align-center">
                    <span>{{ packageData.phone }}</span>
                    <v-btn
                      icon="mdi-content-copy"
                      variant="text"
                      size="small"
                      class="ms-2"
                      @click="copyToClipboard(packageData.phone, $t('packages.phone'))"
                    ></v-btn>
                  </v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-col>
            
            <v-col cols="12" md="4">
              <v-list>
                <v-list-subheader class="text-uppercase font-weight-bold">
                  {{ $t('packages.locationInfo') }}
                </v-list-subheader>
                
                <v-list-item v-if="packageData.device_sn">
                  <template v-slot:prepend>
                    <v-icon icon="mdi-devices" color="primary"></v-icon>
                  </template>
                  <v-list-item-title>{{ $t('packages.device') }}</v-list-item-title>
                  <v-list-item-subtitle class="d-flex align-center">
                    <span>{{ packageData.device_sn }}</span>
                    <v-btn
                      icon="mdi-content-copy"
                      variant="text"
                      size="small"
                      class="ms-2"
                      @click="copyToClipboard(packageData.device_sn, $t('packages.device'))"
                    ></v-btn>
                  </v-list-item-subtitle>
                </v-list-item>
                
                <v-list-item v-if="packageData.cell || packageData.cell_number">
                  <template v-slot:prepend>
                    <v-icon icon="mdi-map-marker" color="primary"></v-icon>
                  </template>
                  <v-list-item-title>{{ $t('packages.cell') }}</v-list-item-title>
                  <v-list-item-subtitle>{{ packageData.cell_number || packageData.cell }}</v-list-item-subtitle>
                </v-list-item>
                
                <v-list-item v-if="packageData.community">
                  <template v-slot:prepend>
                    <v-icon icon="mdi-office-building" color="primary"></v-icon>
                  </template>
                  <v-list-item-title>{{ $t('packages.community') }}</v-list-item-title>
                  <v-list-item-subtitle>{{ packageData.community }}</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
          
          <v-divider class="my-4"></v-divider>
          
          <!-- Events Timeline -->
          <v-row v-if="packageData.events && packageData.events.length > 0">
            <v-col cols="12">
              <v-list>
                <v-list-subheader class="text-uppercase font-weight-bold">
                  {{ $t('packages.eventsHistory') }}
                </v-list-subheader>
                
                <div class="d-flex flex-wrap event-groups-container">
                  <div 
                    v-for="(group, groupIndex) in eventGroups" 
                    :key="groupIndex"
                    class="event-group"
                    :class="{ 'ms-4': groupIndex > 0 }"
                  >
                    <v-timeline density="comfortable" align="start" class="events-timeline">
                      <v-timeline-item
                        v-for="(event, eventIndex) in group"
                        :key="`${groupIndex}-${eventIndex}`"
                        :dot-color="getEventColor(event.type)"
                        :icon="getEventIcon(event.type)"
                        size="default"
                      >
                        <div class="d-flex flex-column event-item">
                          <div class="text-subtitle-1 font-weight-medium mb-1">
                            {{ $t(`packages.eventTypes.${event.type}`) }}
                          </div>
                          <div class="text-body-2 text-medium-emphasis">
                            {{ formatEventDate(event.datetime) }}
                          </div>
                          <!-- Photo preview for cell_photo_was_received events -->
                          <div v-if="event.type === 'cell_photo_was_received' && event.photo_url" class="mt-2">
                            <v-img
                              :src="event.photo_url"
                              max-width="200"
                              max-height="150"
                              cover
                              class="rounded-lg cursor-pointer event-photo"
                              @click="openPhotoDialog(event.photo_url)"
                            ></v-img>
                          </div>
                        </div>
                      </v-timeline-item>
                    </v-timeline>
                  </div>
                </div>
              </v-list>
            </v-col>
          </v-row>
        </v-card-text>
      </template>

      <v-card-text v-else class="text-center pa-6">
        <v-icon icon="mdi-alert-circle" color="error" size="large" class="mb-4"></v-icon>
        <div class="text-h6">{{ $t('packages.notFound') }}</div>
        <div class="text-body-1 mt-2">{{ $t('packages.notFoundText') }}</div>
        <v-btn
          color="primary"
          class="mt-4"
          @click="$router.push('/packages')"
        >
          {{ $t('packages.backToList') }}
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- Photo Dialog -->
    <v-dialog v-model="photoDialog" max-width="90vw">
      <v-card>
        <v-card-text class="pa-0">
          <v-img
            :src="selectedPhoto"
            max-height="90vh"
            contain
          ></v-img>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            variant="text"
            @click="photoDialog = false"
          >
            {{ $t('common.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { format } from 'date-fns'
import api from '@/services/api'
import { useSnackbar } from '@/composables/useSnackbar'
import { useUserFormat } from '@/composables/useUserFormat'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { showSnackbar } = useSnackbar()
const { getUserName, getUserPhone } = useUserFormat()

const packageData = ref(null)
const loading = ref(true)
const photoDialog = ref(false)
const selectedPhoto = ref('')

const eventGroups = computed(() => {
  if (!packageData.value || !packageData.value.events || !packageData.value.events.length) {
    return []
  }

  // Exclude unuseful events
  const filteredEvents = packageData.value.events.filter(event =>
    event.type !== 'drop_off_token_used' && event.type !== 'pick_up_token_used' && event.type !== 'order_locked' && event.type !== 'order_finished' && event.type !== 'order_expired'
  )

  const groups = []
  let currentGroup = []

  const sortedEvents = [...filteredEvents].sort((a, b) => 
    new Date(a.datetime) - new Date(b.datetime)
  )

  sortedEvents.forEach(event => {
    if (event.type === 'cell_was_opened') {
      if (currentGroup.length > 0) {
        groups.push(currentGroup)
      }
      currentGroup = [event]
    } else {
      currentGroup.push(event)
    }
  })

  if (currentGroup.length > 0) {
    groups.push(currentGroup)
  }

  return groups
})

const fetchPackageDetails = async () => {
  loading.value = true
  try {
    const response = await api.get(`/api/v1/parcel/${route.params.order_uuid}/`)
    packageData.value = response.data
  } catch (error) {
    console.error('Failed to fetch package details:', error)
    packageData.value = null
  } finally {
    loading.value = false
  }
}

const formatCreatedAt = (dateString) => {
  if (!dateString) return 'N/A'
  try {
    return format(new Date(dateString), 'dd.MM.yyyy HH:mm')
  } catch (error) {
    return dateString
  }
}

const getStatusColor = (status) => {
  const colors = {
    new: 'warning',
    locked: 'info',
    finished: 'success',
    canceled: 'grey-darken-1',
    expired: 'error'
  }
  return colors[status] || 'grey'
}

const getStatusClass = (status) => {
  return {
    'status-chip': true,
    'status-new': status === 'new',
    'status-locked': status === 'locked',
    'status-finished': status === 'finished',
    'status-canceled': status === 'canceled',
    'status-expired': status === 'expired'
  }
}

const getTypeClass = (type) => {
  return {
    'type-chip': true,
    'type-delivery': type === 'delivery',
    'type-storage': type === 'storage',
    'type-shared': type === 'shared'
  }
}

const getTypeColor = (type) => {
  const colors = {
    delivery: 'primary',
    storage: 'secondary',
    shared: 'info'
  }
  return colors[type] || 'grey'
}

const copyToClipboard = async (text, field) => {
  try {
    // If text is an object, convert it to a readable string
    let textToCopy = text
    if (typeof text === 'object' && text !== null) {
      if (text.first_name && text.last_name) {
        textToCopy = `${text.first_name.trim()} ${text.last_name.trim()}`
      } else if (text.first_name) {
        textToCopy = text.first_name.trim()
      } else if (text.last_name) {
        textToCopy = text.last_name.trim()
      } else if (text.full_name) {
        textToCopy = text.full_name.trim()
      } else if (text.phone_number) {
        textToCopy = text.phone_number
      } else if (text.email) {
        textToCopy = text.email
      } else {
        textToCopy = JSON.stringify(text)
      }
    }
    
    await navigator.clipboard.writeText(textToCopy)
    showSnackbar(t('common.copied', { field }))
  } catch (err) {
    showSnackbar(t('common.copyError'), 'error')
  }
}

const formatEventDate = (dateString) => {
  if (!dateString) return 'N/A'
  try {
    return format(new Date(dateString), 'dd.MM.yyyy HH:mm:ss')
  } catch (error) {
    return dateString
  }
}

const getEventColor = (eventType) => {
  const colors = {
    cell_was_opened: 'success',
    cell_was_closed: 'info',
    parcel_created: 'primary',
    parcel_updated: 'warning',
    parcel_expired: 'error',
    parcel_finished: 'success',
    parcel_canceled: 'grey-darken-1',
    cell_photo_was_received: 'purple'
  }
  return colors[eventType] || 'grey'
}

const getEventIcon = (eventType) => {
  const icons = {
    cell_was_opened: 'mdi-door-open',
    cell_was_closed: 'mdi-door-closed',
    parcel_created: 'mdi-package-variant-plus',
    parcel_updated: 'mdi-package-variant-closed',
    parcel_expired: 'mdi-clock-alert',
    parcel_finished: 'mdi-check-circle',
    parcel_canceled: 'mdi-cancel',
    cell_photo_was_received: 'mdi-camera'
  }
  return icons[eventType] || 'mdi-information'
}

const openPhotoDialog = (photoUrl) => {
  selectedPhoto.value = photoUrl
  photoDialog.value = true
}

onMounted(() => {
  fetchPackageDetails()
})
</script>

<style scoped>
.status-chip {
  font-weight: 500;
  text-transform: capitalize;
}

.status-locked {
  background-color: rgba(var(--v-theme-info), 0.1) !important;
}

.status-new {
  background-color: rgba(var(--v-theme-warning), 0.1) !important;
}

.status-finished {
  background-color: rgba(var(--v-theme-success), 0.1) !important;
}

.status-canceled {
  background-color: rgba(var(--v-theme-grey-darken-1), 0.1) !important;
}

.status-expired {
  background-color: rgba(var(--v-theme-error), 0.1) !important;
}

.type-chip {
  font-weight: 500;
  text-transform: capitalize;
}

.type-delivery {
  background-color: rgba(var(--v-theme-primary), 0.1) !important;
}

.type-storage {
  background-color: rgba(var(--v-theme-secondary), 0.1) !important;
}

.type-shared {
  background-color: rgba(var(--v-theme-info), 0.1) !important;
}

/* Стили для временной шкалы */
:deep(.v-timeline) {
  padding-top: 0;
}

:deep(.v-timeline-item__body) {
  padding: 16px 0;
}

:deep(.v-timeline-item__dot) {
  margin-top: 12px;
}

.events-timeline {
  margin-top: 16px;
}

.event-item {
  padding: 8px 0;
}

:deep(.v-timeline-divider) {
  min-height: 24px;
}

.event-groups-container {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  padding-bottom: 16px;
}

.event-group {
  flex: 0 0 auto;
  min-width: 280px;
  max-width: 320px;
  padding-left: 16px;
  margin-right: 24px;
  position: relative;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border-radius: 8px;
  padding: 8px 16px;
}

.event-group:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  background-color: rgba(var(--v-theme-surface-variant), 0.05);
}

.event-group:first-child {
  padding-left: 0;
}

.event-group:not(:first-child)::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 2px;
  background: linear-gradient(to bottom, 
    rgba(var(--v-theme-primary), 0.7),
    rgba(var(--v-theme-primary), 0.3),
    rgba(var(--v-theme-primary), 0.1)
  );
  border-radius: 2px;
  box-shadow: 0 0 8px rgba(var(--v-theme-primary), 0.2);
}

.event-photo {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.1);
  transition: transform 0.2s ease-in-out;
}

.event-photo:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.cursor-pointer {
  cursor: pointer;
}

.user-info {
  line-height: 1.2;
}

.user-name-line {
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), 0.9);
  margin-bottom: 2px;
}

.user-phone-line {
  font-size: 0.875rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
  font-family: monospace;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
}
</style> 